import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { listingsAPI, metaAPI } from '../lib/api';
import { MapPin, Filter, X, Loader2, ChevronDown } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icons (leaflet issue with bundlers)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Durham Region city coordinates (approximate centers)
const CITY_COORDS = {
  'Pickering': [43.8354, -79.0890],
  'Ajax': [43.8509, -79.0204],
  'Whitby': [43.8975, -78.9429],
  'Courtice': [43.8878, -78.8117],
  'Oshawa': [43.8971, -78.8658],
  'Bowmanville': [43.9126, -78.6872],
};

// Durham Region center
const DURHAM_CENTER = [43.88, -78.88];
const DURHAM_ZOOM = 11;

const promotedIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [30, 48],
  iconAnchor: [15, 48],
  popupAnchor: [0, -48],
  className: 'promoted-marker',
});

const defaultIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [22, 36],
  iconAnchor: [11, 36],
  popupAnchor: [0, -36],
});

function jitter(base, index) {
  // Spread listings within a city area so pins don't overlap
  const seed = index * 2654435761;
  const offsetLat = ((seed % 1000) / 1000 - 0.5) * 0.015;
  const offsetLng = (((seed >> 10) % 1000) / 1000 - 0.5) * 0.02;
  return [base[0] + offsetLat, base[1] + offsetLng];
}

function FitBoundsOnFilter({ markers }) {
  const map = useMap();
  useEffect(() => {
    if (markers.length === 0) return;
    const bounds = L.latLngBounds(markers.map(m => m.position));
    map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
  }, [markers, map]);
  return null;
}

const PLACEHOLDER_IMG = "https://images.pexels.com/photos/8135269/pexels-photo-8135269.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function MapPage() {
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    metaAPI.getCategories().then(r => setCategories(r.data)).catch(() => {});
    listingsAPI.getAll({ limit: 200 }).then(r => {
      const all = [...(r.data.sponsored || []), ...(r.data.listings || [])];
      setListings(all);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filteredListings = useMemo(() => {
    if (!selectedCategory) return listings;
    return listings.filter(l => l.category === selectedCategory);
  }, [listings, selectedCategory]);

  const markers = useMemo(() => {
    return filteredListings
      .filter(l => CITY_COORDS[l.city])
      .map((l, i) => {
        const base = CITY_COORDS[l.city];
        const pos = jitter(base, i);
        const isPromoted = l.promoted && l.promoted_until && new Date(l.promoted_until) > new Date();
        return { listing: l, position: pos, promoted: isPromoted };
      });
  }, [filteredListings]);

  return (
    <div className="h-[calc(100vh-64px)] relative" data-testid="map-page">
      {/* Filter Bar */}
      <div className="absolute top-4 left-4 z-[1000] flex gap-2" data-testid="map-filters">
        <button
          onClick={() => setFilterOpen(!filterOpen)}
          className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-full shadow-lg border border-zinc-200 text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors"
          data-testid="map-filter-btn"
        >
          <Filter className="w-4 h-4" />
          {selectedCategory || 'All Categories'}
          <ChevronDown className="w-3 h-3" />
        </button>
        {selectedCategory && (
          <button
            onClick={() => { setSelectedCategory(''); setFilterOpen(false); }}
            className="flex items-center gap-1 px-3 py-2.5 bg-[#002FA7] text-white rounded-full shadow-lg text-sm font-bold"
            data-testid="map-clear-filter"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
        {filterOpen && (
          <div className="absolute top-12 left-0 bg-white rounded-xl shadow-xl border border-zinc-200 py-2 w-56 max-h-80 overflow-y-auto" data-testid="map-filter-dropdown">
            <button onClick={() => { setSelectedCategory(''); setFilterOpen(false); }}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-zinc-50 ${!selectedCategory ? 'font-bold text-[#002FA7]' : 'text-zinc-700'}`}
            >All Categories</button>
            {categories.map(cat => (
              <button key={cat.id || cat.label} onClick={() => { setSelectedCategory(cat.id || cat.label); setFilterOpen(false); }}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-zinc-50 capitalize ${selectedCategory === (cat.id || cat.label) ? 'font-bold text-[#002FA7]' : 'text-zinc-700'}`}
              >{cat.label || cat.id}</button>
            ))}
          </div>
        )}
      </div>

      {/* Stats Badge */}
      <div className="absolute top-4 right-4 z-[1000] bg-white/90 backdrop-blur rounded-full px-4 py-2 shadow-lg border border-zinc-200" data-testid="map-stats">
        <span className="text-sm font-bold text-zinc-900">{markers.length}</span>
        <span className="text-sm text-zinc-500 ml-1">listing{markers.length !== 1 ? 's' : ''} on map</span>
      </div>

      {/* Loading */}
      {loading && (
        <div className="absolute inset-0 z-[1001] bg-white/80 flex items-center justify-center" data-testid="map-loading">
          <div className="flex flex-col items-center">
            <Loader2 className="w-8 h-8 text-[#002FA7] animate-spin mb-2" />
            <p className="text-sm text-zinc-500">Loading listings...</p>
          </div>
        </div>
      )}

      {/* Map */}
      <MapContainer
        center={DURHAM_CENTER}
        zoom={DURHAM_ZOOM}
        className="h-full w-full"
        style={{ height: '100%', width: '100%' }}
        data-testid="leaflet-map"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <FitBoundsOnFilter markers={markers} />
        {markers.map((m) => (
          <Marker
            key={m.listing.listing_id}
            position={m.position}
            icon={m.promoted ? promotedIcon : defaultIcon}
          >
            <Popup minWidth={220} maxWidth={280}>
              <div className="font-sans" style={{ minWidth: 200 }}>
                <Link to={`/listing/${m.listing.listing_id}`} className="block">
                  <img
                    src={m.listing.images?.[0] || PLACEHOLDER_IMG}
                    alt={m.listing.title}
                    className="w-full h-28 object-cover rounded-lg mb-2"
                    onError={(e) => { e.target.src = PLACEHOLDER_IMG; }}
                  />
                  <p className="text-base font-black text-zinc-900 mb-0.5">${m.listing.price?.toFixed(2)}</p>
                  <p className="text-sm font-semibold text-zinc-800 line-clamp-1">{m.listing.title}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500">
                    <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {m.listing.city}</span>
                    <span className="capitalize">{m.listing.category}</span>
                  </div>
                  {m.promoted && (
                    <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#002FA7] text-white">Featured</span>
                  )}
                </Link>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
